import React from 'react';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';

const FormSelect = ({
	name,
	control,
	label,
	options = [],
	placeholder = 'Select an option',
	required = false,
	disabled = false,
	multiple = false,
	validate,
	rules = {},
	className = '',
	helpText,
	optionValue = 'value',
	optionLabel = 'label',
	...props
}) => {
	// Build validation rules
	const validationRules = {
		required: required
			? typeof required === 'string'
				? required
				: `${label} is required`
			: false,
		...(validate && { validate }),
		...rules,
	};

	return (
		<Controller
			name={name}
			control={control}
			rules={validationRules}
			render={({ field, fieldState: { error } }) => (
				<div className={`mb-3 ${className}`}>
					{label && (
						<label htmlFor={name} className="form-label">
							{label} {required && <span className="text-danger">*</span>}
						</label>
					)}

					<select
						{...field}
						{...props}
						id={name}
						className={`form-select ${error ? 'is-invalid' : ''}`}
						disabled={disabled}
						multiple={multiple}
					>
						{!multiple && placeholder && (
							<option value="">{placeholder}</option>
						)}

						{options.map((option, index) => {
							// Handle different option formats
							let value, displayLabel;

							if (typeof option === 'string' || typeof option === 'number') {
								value = option;
								displayLabel = option;
							} else if (typeof option === 'object') {
								value = option[optionValue] || option.value || option.id;
								displayLabel =
									option[optionLabel] ||
									option.label ||
									option.name ||
									option.text;
							}

							return (
								<option key={`${name}-option-${index}`} value={value}>
									{displayLabel}
								</option>
							);
						})}
					</select>

					{error && <div className="invalid-feedback">{error.message}</div>}

					{helpText && !error && <div className="form-text">{helpText}</div>}
				</div>
			)}
		/>
	);
};

FormSelect.propTypes = {
	name: PropTypes.string.isRequired,
	control: PropTypes.object.isRequired,
	label: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.number,
			PropTypes.shape({
				value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				label: PropTypes.string,
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				name: PropTypes.string,
				text: PropTypes.string,
			}),
		])
	),
	placeholder: PropTypes.string,
	required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	disabled: PropTypes.bool,
	multiple: PropTypes.bool,
	validate: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
	rules: PropTypes.object,
	className: PropTypes.string,
	helpText: PropTypes.string,
	optionValue: PropTypes.string,
	optionLabel: PropTypes.string,
};

export default FormSelect;
